import React, { ReactNode } from 'react'
import {
  Dimensions,
  Image,
  ImageSourcePropType,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { authImage, iconApp } from '@assets'
import { Screen } from './Screen'
import { Icon, Text } from '../atoms'
import { MIN_BIG_DEVICE_HEIGHT } from '@utils'
import { useScreen } from '@hooks'

type ScreenAuthProps = {
  children: ReactNode
  title: string
  isPending?: boolean
  onPressBackIcon?: () => void
  // Web uniquement : nom de la marque + slogan affichés sous le logo pour
  // habiller l'écran et équilibrer l'espace. Ignorés sur mobile / natif.
  brandName?: string
  tagline?: string
  // Web grand écran uniquement : si fourni, l'écran passe en « split » —
  // grande image plein écran à gauche, formulaire à droite. Ignoré sur mobile.
  heroImage?: ImageSourcePropType
}

const heightAuthImage = 200
const heightIconApp = 110

const { height } = Dimensions.get('window')

export const ScreenAuth = ({
  children,
  title,
  isPending,
  onPressBackIcon,
  brandName,
  tagline,
  heroImage,
}: ScreenAuthProps): React.ReactElement => {
  const theme = useTheme()
  const { top } = useSafeAreaInsets()
  const { isWeb, isMobile } = useScreen()

  // Sur le web en écran large, l'image (780×464) était étirée sur toute la
  // largeur puis agrandie au-delà de sa taille réelle -> pixelisée. On la
  // limite à la largeur du formulaire (600) et on la centre. Mobile inchangé.
  const isWideWeb = isWeb && !isMobile
  const styles = getStyle(theme, top, isWideWeb)

  const keyboardVerticalOffset =
    Platform.OS === 'ios' ? (height > MIN_BIG_DEVICE_HEIGHT ? 30 : 0) : 20

  // Web grand écran + heroImage : mise en page « split » (grande image à gauche,
  // formulaire à droite). Uniquement pour les écrans qui passent heroImage.
  if (isWideWeb && heroImage) {
    return (
      <Screen noPaddingTop noPaddingBottom isPending={isPending} statusBarStyle="light">
        <View style={styles.splitRow}>
          <Image source={heroImage} style={styles.splitImage} resizeMode="cover" />
          <View style={styles.splitFormCol}>
            <ScrollView contentContainerStyle={styles.splitScroll}>
              <Image source={iconApp} style={styles.splitLogo} />
              {brandName ? (
                <Text type="titleBigBold" color={theme.colors.primary} center>
                  {brandName}
                </Text>
              ) : null}
              {tagline ? (
                <Text type="body" color={theme.colors.greyBlue} center style={styles.tagline}>
                  {tagline}
                </Text>
              ) : null}
              <View style={styles.splitFormInner}>{children}</View>
            </ScrollView>
          </View>
        </View>
      </Screen>
    )
  }

  return (
    <Screen noPaddingTop isPending={isPending} statusBarStyle="light" paddingBottom>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        // Android 16 (API 36): edge-to-edge is enforced and `adjustResize` no longer
        // shrinks the window on its own (see android/.../styles.xml), so this view
        // must resize itself instead — `undefined` used to be a safe passthrough,
        // it is now a no-op that leaves the keyboard covering the form.
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={keyboardVerticalOffset}
      >
        <ScrollView contentContainerStyle={{ flexGrow: 1 }}>
          <Image source={authImage} style={styles.authImage} resizeMode="cover" />
          <Image source={iconApp} style={styles.iconApp} />

          <View style={styles.contentContainer}>
            {isWideWeb && brandName ? (
              <View style={styles.brandBlock}>
                <Text type="titleBigBold" color={theme.colors.primary} center>
                  {brandName}
                </Text>
                {tagline ? (
                  <Text
                    type="body"
                    color={theme.colors.greyBlue}
                    center
                    style={styles.tagline}
                  >
                    {tagline}
                  </Text>
                ) : null}
              </View>
            ) : null}

            {/* Web + marque : le nom/slogan remplace le titre « Se connecter »
                (redondant). Mobile et autres écrans : titre conservé. */}
            {isWideWeb && brandName ? null : (
              <Text type="titleBold" color={theme.colors.primary}>
                {title}
              </Text>
            )}

            {children}
          </View>

          {onPressBackIcon && (
            <Icon
              name="arrowBack"
              testID="auth-back"
              containerStyle={styles.backIcon}
              color={theme.colors.white}
              onPress={onPressBackIcon}
            />
          )}
        </ScrollView>
      </KeyboardAvoidingView>
    </Screen>
  )
}

const getStyle = (theme: Theme, top: number, isWideWeb: boolean) =>
  StyleSheet.create({
    // Web « split » : grande image à gauche, formulaire à droite.
    splitRow: {
      flex: 1,
      flexDirection: 'row',
    },
    splitImage: {
      flex: 1,
      height: '100%',
    },
    splitFormCol: {
      flex: 1,
      backgroundColor: theme.colors.white,
    },
    splitScroll: {
      flexGrow: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: theme.spacing.xxxl,
      paddingVertical: theme.spacing.xxl,
    },
    splitLogo: {
      width: 96,
      height: 96,
      marginBottom: theme.spacing.md,
    },
    splitFormInner: {
      width: '100%',
      maxWidth: 420,
      marginTop: theme.spacing.lg,
      alignItems: 'stretch',
    },
    contentContainer: {
      flex: 1,
      alignItems: 'center',
      paddingTop: heightAuthImage / 4 + theme.spacing.xl,
      paddingHorizontal: theme.spacing.xl,
      // Web large : on centre verticalement le contenu pour supprimer le grand
      // vide entre le formulaire et le bouton, et équilibrer haut/bas.
      ...(isWideWeb ? { justifyContent: 'center', paddingBottom: theme.spacing.xxxl } : {}),
    },
    brandBlock: {
      alignItems: 'center',
      marginBottom: theme.spacing.xl,
    },
    tagline: {
      marginTop: theme.spacing.xs,
      maxWidth: 420,
    },
    authImage: {
      width: '100%',
      height: heightAuthImage + top,
      // Web large : on cadre l'image comme le formulaire (600) et on la centre
      // pour qu'elle ne soit plus étirée ni pixelisée. Coins arrondis = plus net.
      ...(isWideWeb
        ? {
            maxWidth: 600,
            alignSelf: 'center',
            borderBottomLeftRadius: theme.border.medium,
            borderBottomRightRadius: theme.border.medium,
          }
        : {}),
    },
    iconApp: {
      position: 'absolute',
      top: heightAuthImage + top - heightAuthImage / 4,
      alignSelf: 'center',
      width: heightIconApp,
      height: heightIconApp,
    },
    backIcon: {
      position: 'absolute',
      left: '20%',
      marginLeft: -(heightIconApp / 2),
      top: heightAuthImage + top - heightAuthImage / 13,
      padding: theme.spacing.xxs,
      borderRadius: theme.border.jumbo,
      backgroundColor: theme.colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
      width: theme.iconSize.jumbo,
      height: theme.iconSize.jumbo,
    },
  })
